import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Calendar, Loader2 } from 'lucide-react';
import { getApiUrl, apiClient } from '../utils/api';
import { 
  CreateActivityData,
  getActivityTypes,
  getEmployeeActivityTypes,
  ActivityType,
  validateActivityDates
} from '../services';
import { DateInput } from '../components/DateInput';
import { TimeInput } from '../components/TimeInput';

export function ActivityCreatePage() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<CreateActivityData>({
    employeeId: '',
    shopId: '',
    activityTypeId: '',
    employeeActivityTypeId: '',
    title: '',
    description: '',
    colorOverride: null,
    startDateTime: '',
    endDateTime: '',
    isAllDay: false,
    isBlockingOverride: null,
    visibility: 'INTERNAL',
    status: 'PLANNED',
    timezone: 'Europe/Paris',
  });
  
  const [shops, setShops] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [employeeActivityTypes, setEmployeeActivityTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!formData.activityTypeId && activityTypes.length > 0) {
      const defaultType = activityTypes.find((type) => type.isDefault);
      if (defaultType) {
        setFormData((prev) => ({
          ...prev,
          activityTypeId: defaultType.id,
        }));
      }
    }
  }, [activityTypes, formData.activityTypeId]);

  useEffect(() => {
    if (formData.shopId) {
      loadEmployees();
    }
  }, [formData.shopId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [shopsRes, activityTypesRes, employeeTypesRes] = await Promise.all([
        fetch(getApiUrl('/api/admin/shops?limit=1000'), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        getActivityTypes(),
        getEmployeeActivityTypes(),
      ]);

      const shopsData = await shopsRes.json();
      if (shopsData.success) {
        setShops(shopsData.data.shops || []);
      }
      
      setActivityTypes(activityTypesRes);
      setEmployeeActivityTypes(employeeTypesRes);
    } catch (error) {
      console.error('Échec du chargement des données initiales:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    if (!formData.shopId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/employees?shopId=${formData.shopId}&limit=200`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setEmployees(
          (data.data?.employees || []).map((e: any) => ({
            id: e.id,
            fullName:
              e.person?.displayName ||
              `${e.person?.firstName || ''} ${e.person?.lastName || ''}`.trim() ||
              e.person?.email ||
              'Employé',
          }))
        );
      }
    } catch (error) {
      console.error('Échec du chargement des employés:', error);
    }
  };

  const handleInputChange = (field: keyof CreateActivityData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (field: 'startDateTime' | 'endDateTime', dateValue: string, timeValue: string) => {
    if (formData.isAllDay) {
      // For all-day, just use the date with time at midnight (ISO format)
      const dateTime = `${dateValue}T00:00:00.000Z`;
      handleInputChange(field, dateTime);
    } else {
      // Combine date and time, convert to ISO format
      const dateTime = `${dateValue}T${timeValue}:00.000Z`;
      handleInputChange(field, dateTime);
    }
  };

  const getSelectedActivityType = (): ActivityType | undefined => {
    return activityTypes.find(t => t.id === formData.activityTypeId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.employeeId || !formData.shopId || !formData.activityTypeId || !formData.title) {
      setError('Veuillez remplir tous les champs obligatoires');
      setErrorDetails([]);
      return;
    }

    if (!formData.startDateTime || !formData.endDateTime) {
      setError('Veuillez sélectionner les dates de début et de fin');
      setErrorDetails([]);
      return;
    }

    // Validate dates
    const validation = validateActivityDates(formData.startDateTime, formData.endDateTime);
    if (!validation.valid) {
      setError(validation.error || 'Dates invalides');
      setErrorDetails([]);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setErrorDetails([]);
      
      const response = await apiClient('/api/planning/activities', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          employeeActivityTypeId: formData.employeeActivityTypeId || undefined,
        }),
      });
      const data = await response.json();
      
      if (!data.success) {
        setError(data.message || 'Échec de la création de l\'activité');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
        return;
      }
      
      navigate('/planning/activities');
    } catch (error: any) {
      console.error('Create activity error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  const selectedType = getSelectedActivityType();
  const startDate = formData.startDateTime ? formData.startDateTime.split('T')[0] : '';
  const startTime = formData.startDateTime && !formData.isAllDay ? formData.startDateTime.split('T')[1]?.substring(0, 5) : '09:00';
  const endDate = formData.endDateTime ? formData.endDateTime.split('T')[0] : '';
  const endTime = formData.endDateTime && !formData.isAllDay ? formData.endDateTime.split('T')[1]?.substring(0, 5) : '18:00';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/planning/activities')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <Calendar className="w-6 h-6" />
              <span>Créer une Activité</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-xl font-semibold text-textPrimary mb-6">Informations Générales</h2>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Boutique *
                </label>
                <select
                  value={formData.shopId ?? ''}
                  onChange={(e) => handleInputChange('shopId', e.target.value)}
                  className="input-field"
                  required
                >
                  <option value="">Sélectionner une boutique</option>
                  {shops.map(shop => (
                    <option key={shop.id} value={shop.id}>{shop.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Employé *
                </label>
                <select
                  value={formData.employeeId || ''}
                  onChange={(e) => handleInputChange('employeeId', e.target.value)}
                  className="input-field"
                  required
                  disabled={!formData.shopId}
                >
                  <option value="">Sélectionner un employé</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.fullName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Type d'Activité *
                </label>
                <select
                  value={formData.activityTypeId || ''}
                  onChange={(e) => handleInputChange('activityTypeId', e.target.value)}
                  className="input-field"
                  required
                >
                  <option value="">Sélectionner un type</option>
                  {activityTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Type d'Activité Employé (optionnel)
                </label>
                <select
                  value={formData.employeeActivityTypeId || ''}
                  onChange={(e) => handleInputChange('employeeActivityTypeId', e.target.value || null)}
                  className="input-field"
                >
                  <option value="">Aucun type employé</option>
                  {employeeActivityTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Titre *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="input-field"
                  placeholder="Ex: Pause déjeuner"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="input-field"
                placeholder="Description de l'activité..."
              />
            </div>
          </div>
        </div>

        {/* Date & Time */}
        <div className="card">
          <h2 className="text-xl font-semibold text-textPrimary mb-6">Date et Heure</h2>

          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isAllDay}
                  onChange={(e) => handleInputChange('isAllDay', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-textPrimary">
                  Journée entière
                </span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Date de Début *
                </label>
                <DateInput
                  value={startDate}
                  onChange={(value) => handleDateChange('startDateTime', value, startTime)}
                  className="input-field"
                  required
                />
              </div>

              {!formData.isAllDay && (
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Heure de Début *
                  </label>
                  <TimeInput
                    value={startTime}
                    onChange={(value) => handleDateChange('startDateTime', startDate, value)}
                    className="input-field"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Date de Fin *
                </label>
                <DateInput
                  value={endDate}
                  onChange={(value) => handleDateChange('endDateTime', value, endTime)}
                  className="input-field"
                  required
                />
              </div>

              {!formData.isAllDay && (
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Heure de Fin *
                  </label>
                  <TimeInput
                    value={endTime}
                    onChange={(value) => handleDateChange('endDateTime', endDate, value)}
                    className="input-field"
                    required
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="card">
          <h2 className="text-xl font-semibold text-textPrimary mb-6">Options</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Bloquer les réservations
              </label>
              <select
                value={formData.isBlockingOverride === null ? 'default' : formData.isBlockingOverride ? 'true' : 'false'}
                onChange={(e) => {
                  const value = e.target.value === 'default' ? null : e.target.value === 'true';
                  handleInputChange('isBlockingOverride', value);
                }}
                className="input-field"
              >
                <option value="default">
                  Par défaut ({selectedType?.isBlocking ? 'Oui' : 'Non'})
                </option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Visibilité
              </label>
              <select
                value={formData.visibility}
                onChange={(e) => handleInputChange('visibility', e.target.value as any)}
                className="input-field"
              >
                <option value="INTERNAL">Interne (backoffice uniquement)</option>
                <option value="FRONTOFFICE">Public (visible aux clients)</option>
                <option value="PRIVATE">Privé (praticien + admins)</option>
              </select>
            </div>

            {selectedType && (
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Couleur (optionnel, laisse vide pour utiliser la couleur par défaut)
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={formData.colorOverride || selectedType.defaultColor}
                    onChange={(e) => handleInputChange('colorOverride', e.target.value)}
                    className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.colorOverride || ''}
                    onChange={(e) => handleInputChange('colorOverride', e.target.value || null)}
                    className="input-field flex-1"
                    placeholder={selectedType.defaultColor}
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                  <button
                    type="button"
                    onClick={() => handleInputChange('colorOverride', null)}
                    className="btn-secondary text-sm"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/planning/activities')}
            className="btn-secondary"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center space-x-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Création...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Créer l'Activité</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

